import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { Router, ActivatedRoute } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { FormGroup, Validators, FormArray } from '@angular/forms';

import { ModuleConfiguration } from '../dynamicDataSet.module.config';
import { DynamicDataSetModel } from '../models/DynamicDataSetModel';
import * as fromStore from '../store/reducers';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import { PhysicalAddressModel } from '../../common/models/PhysicalAddressModel';
import { AutoSaveFormBuilder } from '../../pillar/workspace/AutoSaveFormBuilder';
import { AbstractControl } from '@angular/forms/src/model';
import { Subject } from 'rxjs/Subject';
import { AutoSaveFormGroup } from '../../pillar/workspace/AutoSaveFormGroup';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { MatDialog, MatCheckboxChange } from '@angular/material';
import { DeleteDynamicDataSetConfirmationDialogComponent } from './delete-dynamic-dataset-confirmation-dialog.component';
import { DynamicEntityModel } from '../models/DynamicEntityModel';
import { PropertyModel } from '../models/PropertyModel';
import { DataLinkModel } from '../models/DataLinkModel';
import { FinalizeDynamicDataSetAction } from '../store/actions/FinalizeDynamicDataSetAction';
import { LookupModel } from '../../common/models/LookupModel';
import { DataWarehouseService } from '../../core/services';
import { DynamicRuleProfileModel } from '../models/DynamicRuleProfileModel';
import { DynamicRuleModel } from '../models/DynamicRuleModel';
import { RuleExpressionModel } from '../models/RuleExpressionModel';
import { LogicalExpressionModel } from '../models/LogicalExpressionModel';
import { GROUP_RULE_CONDITIONS_SUCCESS, GroupRuleConditionsSuccessAction } from '../store/actions/GroupRuleConditionsSuccessAction';
import { GroupRuleConditionsAction } from '../store/actions/GroupRuleConditionsAction';
import { AddRuleConditionGroupModel } from '../models/AddRuleConditionGroupModel';
import { UngroupRuleConditionsAction } from '../store/actions/UngroupRuleConditionsAction';
import { UNGROUP_RULE_CONDITIONS_SUCCESS, UngroupRuleConditionsSuccessAction } from '../store/actions/UngroupRuleConditionsSuccessAction';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'brd-dynamic-rule-workspace',
  templateUrl: './dynamic-rule-workspace.component.html',
  styleUrls: ['./dynamic-rule-workspace.component.scss']
})
export class DynamicRuleWorkspaceComponent {
  dynamicRule: DynamicRuleModel;
  key: string;
  dataSetKey: string;
  loading$: Observable<boolean>;
  reportingNotConfigured$: Observable<boolean>;
  errors$: Observable<string[]>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  profileForm: FormGroup;
  expressionForm: FormGroup;
  conditionsForm: FormGroup;
  formArrayInitMethods = new Map<string, () => FormGroup>();
  dataSetEntityProperties: Observable<LookupModel[]>;
  compareType: string;
  readOnly: boolean;
  groupSelection: FormGroup[] = [];

  editPermission: string = 'DynamicDataSetModule.DynamicDataSets:Edit';

  operators: string[] = ['And', 'Or'];
  operations: string[] = [
    'Empty',
    'EqualTo',
    'ExclusiveBetween',
    'GreaterThan',
    'GreaterThanOrEqualTo',
    'InclusiveBetween',
    'LessThan',
    'LessThanOrEqualTo',
    'MatchesRegex',
    'MatchesRegexOrNull',
    'MaxLength',
    'MinLength',
    'NotEmpty',
    'NotEqualTo',
    'NotNull',
    'Null'
  ];
  compareTypes = ['Literal', 'Field', 'LinkedField'];
  logicalOperations: string[] = ['==', '!=', '<', '<=', '>', '>='];
  boolOptions = [{ key: 'true', description: 'True' }, { key: 'false', description: 'False' }];



  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<any>,
    private router: Router,
    private formBuilder: AutoSaveFormBuilder,
    private actions$: Actions,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private dataWarehouseService: DataWarehouseService) {
  }


  ngOnDestroy() {
    this.store.dispatch(new dashboardActions.ClearSelectionAction(ModuleConfiguration.moduleName, DynamicRuleModel));
    this.destroyed$.next(true);
  }

  ngOnInit() {
    this.loading$ = this.store.select(fromStore.getDynamicRuleListLoading);
    this.errors$ = this.store.select(fromStore.getDynamicRuleListErrors);
    this.route.params.take(1).subscribe(p => {
      this.key = p['key'];
      this.dataSetKey = p['dataSetKey'];

      this.store.dispatch(new dashboardActions.GetSelectionAction(ModuleConfiguration.moduleName, DynamicRuleModel, { key: this.dataSetKey, subKey: this.key }));
    });

    var updateTypes = [
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, DynamicRuleModel, RuleExpressionModel),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, DynamicRuleModel, LogicalExpressionModel),
      workspaceActions.updateModelType(ModuleConfiguration.moduleName, DynamicEntityModel, DynamicRuleProfileModel)
    ];
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelSuccessTypeByUpdateType(t))))
      .subscribe((a: workspaceActions.UpdateModelSuccessAction<DynamicDataSetModel, any>) => {
        a.payload.control.saveSuccess(a.payload.model);
      });
    this.actions$
      .takeUntil(this.destroyed$)
      .filter(a => Boolean(updateTypes.find(t => a.type === workspaceActions.updateModelErrorTypeByUpdateType(t))))
      .subscribe((a: workspaceActions.UpdateModelErrorAction<DynamicDataSetModel, any>) => {
        a.payload.action.payload.control.saveErrors(a.payload.action.payload.model, a.payload.errors);
      });
    this.actions$.ofType(workspaceActions.removeModelSuccessType(ModuleConfiguration.moduleName, DynamicRuleModel, LogicalExpressionModel))
      .takeUntil(this.destroyed$)
      .map((a: any) => a.payload)
      .subscribe((payload) => {
        payload.control.saveSuccess(payload.model);
        var array = (this.conditionsForm.controls['conditions'] as FormArray);
        var index = array.controls.indexOf(payload.control);
        array.removeAt(index);
      });

    this.actions$.ofType(GROUP_RULE_CONDITIONS_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: GroupRuleConditionsSuccessAction) => {
        this.applyGroup(a.payload.groupName);
      });
    this.actions$.ofType(UNGROUP_RULE_CONDITIONS_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe((a: UngroupRuleConditionsSuccessAction) => {
        this.applyUngroup(a.payload.groupName);
      });

    this.store.select(fromStore.getDynamicRuleSelection)
      .takeUntil(this.destroyed$)
      .subscribe((v: DynamicRuleModel) => {
        if (v) {
          this.dynamicRule = v;
          if (!this.profileForm) {
            this.initForms();
          } else {
            this.updateFormValues();
          }
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  initForms(): void {
    this.profileForm = this.formBuilder.group({
      key: [''],
      name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
      isEnabled: ['', Validators.required]
    },
      null,
      this.destroyed$,
      (control) => {
        this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, DynamicEntityModel, DynamicRuleProfileModel, { key: this.dynamicRule.entity.dataSetKey, model: control.value, control: control }));
      },
      this.changeDetectorRef);
    this.expressionForm = this.initExpression(this.dynamicRule.expression);
    this.conditionsForm = this.formBuilder.group({
      conditions: this.formBuilder.array(this.dynamicRule.conditions.map(c => this.initCondition(c)))
    });

    this.formArrayInitMethods.set('conditions', () => this.initCondition());
    setTimeout(() => {
        this.updateFormValues();
      },
      50);
  }

  updateFormValues(): void {

    if (this.dynamicRule.dataSetStatus === 'Draft') {
      this.profileForm.enable();
      this.expressionForm.enable();
      this.conditionsForm.enable();
      this.readOnly = false;
      this.profileForm.patchValue(this.dynamicRule);
      this.profileForm.markAsPristine();
      this.expressionForm.patchValue(this.dynamicRule.expression || {});
      this.expressionForm.markAsPristine();
      this.conditionsForm.patchValue(this.dynamicRule);
      this.conditionsForm.markAsPristine();
    } else {
      this.profileForm.patchValue(this.dynamicRule);
      this.profileForm.markAsPristine();
      this.expressionForm.patchValue(this.dynamicRule.expression || {});
      this.expressionForm.markAsPristine();
      this.conditionsForm.patchValue(this.dynamicRule);
      this.conditionsForm.markAsPristine();
      this.profileForm.disable();
      this.expressionForm.disable();
      this.conditionsForm.disable();
      this.readOnly = true;
    }
  }

  private updateCompareType() {

  }

  initExpression(initialValue?: RuleExpressionModel) {
    var initialProperty: PropertyModel;
    var initialized = true;
    if (initialValue) {
      initialized = false;
      initialProperty = this.dynamicRule.entity.properties.find(p => p.key === initialValue.propertyKey);
    }
    var formGroup = this.formBuilder.group({
      key: [this.dynamicRule.key],
      propertyKey: ['', Validators.required],
      propertyType: [initialProperty ? initialProperty.type : ''],
      propertyValueSet: [initialProperty && initialProperty.valueSet ? initialProperty.valueSet.key : ''],
      operationName: ['', Validators.required],
      errorMessage: ['', Validators.compose([Validators.required, Validators.maxLength(500)])]
    },
      null,
      this.destroyed$,
      (control) => {
        this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, DynamicRuleModel, RuleExpressionModel, { key: this.dynamicRule.entity.dataSetKey, model: control.value, control: control }));
      },
      this.changeDetectorRef);
    for (let c in formGroup.controls) {
      formGroup.get(c).markAsTouched();
      formGroup.get(c).updateValueAndValidity();
    }
    formGroup.get('propertyKey')
      .valueChanges
      .subscribe(
      (propertyKey: string) => {
        var property = this.dynamicRule.entity.properties.find(p => p.key === propertyKey);
        if (property) {
          formGroup.get('propertyType').setValue(property.type);
          if (property.valueSet) {
            formGroup.get('propertyValueSet').setValue(property.valueSet.key);
          }
        }
        if (formGroup.get('propertyKey').dirty) {
          formGroup.get('operationName').setValue(null);
        }
      });
    formGroup.get('operationName')
      .valueChanges
      .subscribe(
        (operation: string) => {
          if (operation === 'EqualTo' ||
            operation === 'ExclusiveBetween' ||
            operation === 'GreaterThan' ||
            operation === 'GreaterThanOrEqualTo' ||
            operation === 'InclusiveBetween' ||
            operation === 'LessThan' ||
            operation === 'LessThanOrEqualTo' ||
            operation === 'MatchesRegex' ||
            operation === 'MatchesRegexOrNull' ||
            operation === 'NotEqualTo' ||
            operation === 'MaxLength' ||
            operation === 'MinLength') {
            this.compareTypes = ['Literal', 'Field', 'LinkedField'];
            var compareTypeControl = formGroup.get('compareTypeName');
            if (!compareTypeControl) {
              compareTypeControl = this.formBuilder.control('', Validators.required);
              formGroup.addControl('compareTypeName', compareTypeControl);
              compareTypeControl.valueChanges
                .subscribe(
                  (compareType: string) => {
                    var setValue = !initialized;
                    var operationName = formGroup.get('operationName').value;
                    if (compareType === 'Literal') {
                      if (operationName === 'EqualTo' ||
                        operationName === 'ExclusiveBetween' ||
                        operationName === 'GreaterThan' ||
                        operationName === 'GreaterThanOrEqualTo' ||
                        operationName === 'InclusiveBetween' ||
                        operationName === 'LessThan' ||
                        operationName === 'LessThanOrEqualTo' ||
                        operationName === 'NotEqualTo') {
                        this.compareType = formGroup.get('propertyType').value;
                        if (this.compareType === 'Vocabulary') {
                          formGroup.addControl('compareVocabulary', this.formBuilder.control(setValue ? initialValue.compareVocabulary : '', Validators.required));
                        } else {
                          formGroup.addControl('compareValue1', this.formBuilder.control(setValue ? initialValue.compareValue1 : '', Validators.required));
                        }
                        if (this.compareType !== 'Vocabulary' &&
                          (operationName === 'ExclusiveBetween' ||
                            operationName === 'InclusiveBetween')) {
                          formGroup.addControl('compareValue2', this.formBuilder.control(setValue ? initialValue.compareValue2 : '', Validators.required));
                        }
                        this.clearControls(['comparePropertyKey', 'compareDataLinkKey', 'linkedFieldName'], formGroup);
                      } else if (operationName === 'MatchesRegex' ||
                        operationName === 'MatchesRegexOrNull') {
                        this.compareType = 'string';
                        formGroup.addControl('compareValue1', this.formBuilder.control(setValue ? initialValue.compareValue1 : '', Validators.required));
                        this.clearControls(['compareVocabulary', 'compareValue2', 'comparePropertyKey', 'compareDataLinkKey', 'linkedFieldName'], formGroup);
                      } else if (operationName === 'MaxLength' ||
                        operationName === 'MinLength') {
                        this.compareType = 'int?';
                        formGroup.addControl('compareValue1', this.formBuilder.control(setValue ? initialValue.compareValue1 : '', Validators.required));
                        this.clearControls(['compareVocabulary', 'compareValue2', 'comparePropertyKey', 'compareDataLinkKey', 'linkedFieldName'], formGroup);
                      }
                    } else if (compareType === 'Field') {
                      formGroup.addControl('comparePropertyKey', this.formBuilder.control(setValue ? initialValue.comparePropertyKey : '', Validators.required));
                      this.clearControls(['compareValue1', 'compareVocabulary', 'compareValue2', 'compareDataLinkKey', 'linkedFieldName'], formGroup);
                    } else if (compareType === 'LinkedField') {
                      formGroup.addControl('compareDataLinkKey', this.formBuilder.control(setValue ? initialValue.compareDataLinkKey : '', Validators.required));
                      formGroup.addControl('linkedFieldName', this.formBuilder.control(setValue ? initialValue.linkedFieldName : '', Validators.required));
                      this.clearControls(['compareValue1', 'compareVocabulary', 'compareValue2', 'comparePropertyKey'], formGroup);
                    } else {
                      this.clearControls(['compareValue1', 'compareVocabulary', 'compareValue2', 'comparePropertyKey', 'compareDataLinkKey', 'linkedFieldName'], formGroup);
                    }
                    initialized = true;
                  }
                );
            }

            if (operation === 'ExclusiveBetween' ||
              operation === 'InclusiveBetween' ||
              operation === 'MatchesRegex' ||
              operation === 'MatchesRegexOrNull' ||
              operation === 'MaxLength' ||
              operation === 'MinLength') {
              this.compareTypes = ['Literal'];
            }
            if (formGroup.get('operationName').dirty) {
              if (this.compareTypes.length === 1) {
                compareTypeControl.setValue(this.compareTypes[0]);
              }
              compareTypeControl.setValue(null);
            } else if(!initialized) {
              compareTypeControl.setValue(initialValue.compareTypeName);
            }
          } else {
            this.clearControls(['compareTypeName', 'compareValue1', 'compareVocabulary', 'compareValue2', 'comparePropertyKey', 'compareDataLinkKey', 'linkedFieldName'], formGroup);
          }
        }
      );
    return formGroup;
  }

  private clearControls(names: string[], formGroup: FormGroup) {
    names.forEach(n => {
      if (formGroup.contains(n)) {
        formGroup.removeControl(n);
      }
    });
  }

  private checkAddValue(formGroup: FormGroup, property: PropertyModel, setValue: boolean, initialValue: LogicalExpressionModel) {
    if (property.type === 'Vocabulary') {
      formGroup.addControl('valueVocabulary', this.formBuilder.control(setValue ? initialValue.valueVocabulary : '', Validators.required));
      this.clearControls(['value'], formGroup);
    } else {
      formGroup.addControl('value', this.formBuilder.control(setValue ? initialValue.value : '', Validators.required));
      this.clearControls(['valueVocabulary'], formGroup);
    }
  }

  initCondition(initialValue?: LogicalExpressionModel) {
    var initialProperty: PropertyModel;
    var initialized = true;
    if (initialValue) {
      initialized = false;
      initialProperty = this.dynamicRule.entity.properties.find(p => p.key === initialValue.propertyKey);
    }
    var formGroup = this.formBuilder.group({
      key: [this.dynamicRule.key],
      hashCode: [''],
      groupName: [''],
      operatorName: ['And', Validators.required],
      propertyKey: ['', Validators.required],
      propertyType: [initialProperty ? initialProperty.type : ''],
      propertyValueSet: [initialProperty && initialProperty.valueSet ? initialProperty.valueSet.key : ''],
      operationName: ['', Validators.required]
    },
      null,
      this.destroyed$,
      (control) => {
        this.store.dispatch(new workspaceActions.UpdateModelAction(ModuleConfiguration.moduleName, DynamicRuleModel, LogicalExpressionModel, { key: this.dynamicRule.entity.dataSetKey, model: control.value, control: control }));
      },
      this.changeDetectorRef);
    if (initialProperty) {
      this.checkAddValue(formGroup, initialProperty, true, initialValue);
    }
    formGroup.get('propertyKey')
      .valueChanges
      .subscribe(
      (propertyKey: string) => {
        var property = this.dynamicRule.entity.properties.find(p => p.key === propertyKey);
        if (property) {
          formGroup.get('propertyType').setValue(property.type);
          if (property.valueSet) {
            formGroup.get('propertyValueSet').setValue(property.valueSet.key);
          }

          var setValue = !initialized;
          this.checkAddValue(formGroup, property, setValue, initialValue);
        }
        initialized = true;
        });
    return formGroup;
  }

  addFormGroup(form, name, index?: number, groupName?: string) {
    var formGroup = this.formArrayInitMethods.get(name)();
    if (index) {
      form.controls[name].insert(index + 1, formGroup);
    } else {
      form.controls[name].push(formGroup);
    }
    if (groupName) {
      formGroup.get('groupName').setValue(groupName);
    }
  }

  removeCondition(form, condition: AutoSaveFormGroup, index) {
    condition.initializeRemove();
    this.store.dispatch(new workspaceActions.RemoveModelAction(ModuleConfiguration.moduleName, DynamicRuleModel, LogicalExpressionModel, { key: this.dynamicRule.entity.dataSetKey, model: condition.value, control: condition }));
  }

  back() {
    this.router.navigateByUrl('/dynamic-dataset-workspace/' + this.dynamicRule.entity.dataSetKey);
  }

  checkChanged(event: MatCheckboxChange, condition: FormGroup) {
    if (event.checked) {
      this.addToGroupSelection(condition);
    } else {
      this.removeFromGroupSelection(condition);
    }
  }

  addToGroupSelection(condition: FormGroup) {
    this.groupSelection.push(condition);
  }

  removeFromGroupSelection(condition: FormGroup) {
    var index = this.groupSelection.indexOf(condition);
    if (index > -1) {
      this.groupSelection.splice(index, 1);
    }
  }

  group() {
    var conditions: LogicalExpressionModel[] = this.conditionsForm.value.conditions;
    var nextGroup = Math.max.apply(this, conditions.map(c => parseInt(c.groupName) || 0)) + 1;
    var model = new AddRuleConditionGroupModel();
    model.groupName = nextGroup;
    model.conditionHashCodes = this.groupSelection.map(c => c.value.hashCode);
    this.store.dispatch(new GroupRuleConditionsAction({
      dynamicDataSetKey: this.dynamicRule.entity.dataSetKey,
      dynamicRuleKey: this.key,
      model: model
    }));
  }

  ungroup(groupName: string) {
    this.store.dispatch(new UngroupRuleConditionsAction({
      dynamicDataSetKey: this.dynamicRule.entity.dataSetKey,
      dynamicRuleKey: this.key,
      groupName: groupName
    }));
  }

  applyGroup(groupName: string) {
    this.groupSelection.forEach(c => c.get('groupName').setValue(groupName));
    this.groupSelection = [];
    this.refreshConditions();
  }

  applyUngroup(groupName: string) {
    var conditions = this.conditionsForm.get("conditions") as FormArray;
    conditions.controls.forEach(c => c.get('groupName').setValue(null));
    this.refreshConditions();
  }

  private refreshConditions(): void {
    this.conditionsForm.get('conditions').setValue(this.conditionsForm.get('conditions').value);
  }
}




// WEBPACK FOOTER //
// ./src/app/dynamicDataSets/components/dynamic-rule-workspace.component.ts